import { styled } from '@mui/material';
import ButtonBase, { ButtonBaseProps } from '@mui/material/ButtonBase';
import FormControlLabel from '@mui/material/FormControlLabel';
import MuiRadio from '@mui/material/Radio';

export type RadioButtonSize = 'small' | 'medium';

export interface CustomRadioProps extends ButtonBaseProps {
    border?: boolean;
    checked?: boolean;
    endIcon?: React.JSX.Element;
    fullWidth?: boolean;
    header?: boolean;
    label: string | React.JSX.Element;
    size?: RadioButtonSize;
    onCheck?: (checked: boolean) => void;
}

const StyledButton = styled(ButtonBase, {
    shouldForwardProp: (prop) => prop !== 'border' && prop !== 'fullWidth' && prop !== 'header' && prop !== 'size',
})<{
    border: boolean;
    fullWidth: boolean;
    header: boolean;
    size: RadioButtonSize;
}>(({ border, fullWidth, header, size, theme }) => ({
    backgroundColor: border ? theme.palette.background.paper : undefined,
    border: border ? theme.constants.border : 'none',
    borderRadius: header
        ? `${theme.constants.borderRadius}px ${theme.constants.borderRadius}px 0 0`
        : theme.constants.borderRadius,
    gap: theme.spacing(1),
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    padding: size === 'small' ? theme.spacing(1) : theme.spacing(2),
    width: header || fullWidth ? '100%' : 'unset',
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
    },
}));

const StyledFormControlLabel = styled(FormControlLabel)(({ theme }) => ({
    alignItems: 'flex-start',
    display: 'flex',
    margin: 0,
    gap: theme.spacing(1),
    '& .MuiFormControlLabel-label': {
        margin: 0,
    },
    '& .MuiRadio-root': {
        marginTop: theme.spacing(0.25),
    },
}));

const StyledRadio = styled(MuiRadio)(() => ({
    padding: 0,
    margin: 0,
    '& input:hover': {
        backgroundColor: 'none',
    },
}));

export const Radio: React.FC<CustomRadioProps> = ({
    border = false,
    checked = false,
    disabled = false,
    endIcon,
    fullWidth = false,
    header = false,
    label,
    size = 'small',
    onCheck,
    ...props
}) => {
    return (
        <StyledButton
            border={border}
            disabled={disabled}
            fullWidth={fullWidth}
            header={header}
            size={size}
            onClick={() => onCheck?.(!checked)}
            {...props}
        >
            <StyledFormControlLabel
                control={
                    <StyledRadio
                        checked={checked}
                        disabled={disabled}
                        onChange={(e) => e.preventDefault()}
                        tabIndex={-1}
                    />
                }
                disabled={disabled}
                label={label}
                onClick={(e) => e.preventDefault()}
            />
            {endIcon}
        </StyledButton>
    );
};
